import { useCallback, useMemo, useState } from "react";

const MAX_WIDEN_LEVEL = 3;

export function widenFilters(base, level) {
  if (level <= 0) return base;
  const f = { ...base };

  if (level >= 1) {
    if (f.minBedrooms != null) f.minBedrooms = Math.max(0, Number(f.minBedrooms) - 1);
    if (f.maxBedrooms != null) f.maxBedrooms = Number(f.maxBedrooms) + 1;
    if (f.minRooms != null) f.minRooms = Math.max(0, Number(f.minRooms) - 1);
    if (f.maxRooms != null) f.maxRooms = Number(f.maxRooms) + 1;
    if (f.minSquareMetersCovered != null)
      f.minSquareMetersCovered = Math.max(0, Math.floor(Number(f.minSquareMetersCovered) * 0.7));
    if (f.maxSquareMetersCovered != null)
      f.maxSquareMetersCovered = Math.ceil(Number(f.maxSquareMetersCovered) * 1.3);
    if (f.minSquareMetersTotal != null)
      f.minSquareMetersTotal = Math.max(0, Math.floor(Number(f.minSquareMetersTotal) * 0.7));
    if (f.maxSquareMetersTotal != null)
      f.maxSquareMetersTotal = Math.ceil(Number(f.maxSquareMetersTotal) * 1.3);
    // Sacar amenities (suelen sobre-filtrar)
    f.hasPool = false;
    f.hasGrill = false;
    f.hasGarden = false;
    f.hasTerrace = false;
    f.hasBalcony = false;
  }
  if (level >= 2) {
    // Sacar baños y cocheras
    delete f.minBathrooms;
    delete f.maxBathrooms;
    f.minParking = null;
    // Ensanchar m² más
    if (f.minSquareMetersCovered != null)
      f.minSquareMetersCovered = Math.max(0, Math.floor(Number(f.minSquareMetersCovered) * 0.5));
    if (f.maxSquareMetersCovered != null)
      f.maxSquareMetersCovered = Math.ceil(Number(f.maxSquareMetersCovered) * 1.5);
  }
  if (level >= 3) {
    // Sólo queda el corazón de la búsqueda: zona + tipo + operación.
    delete f.minBedrooms;
    delete f.maxBedrooms;
    delete f.minRooms;
    delete f.maxRooms;
    delete f.minSquareMetersCovered;
    delete f.maxSquareMetersCovered;
    delete f.minSquareMetersTotal;
    delete f.maxSquareMetersTotal;
    delete f.minAntiquity;
    delete f.maxAntiquity;
  }
  return f;
}


const RANGE_FILTERS = [
  { label: "Ambientes", min: "minRooms", max: "maxRooms" },
  { label: "Dormitorios", min: "minBedrooms", max: "maxBedrooms" },
  { label: "Baños", min: "minBathrooms", max: "maxBathrooms" },
  { label: "m² cubiertos", min: "minSquareMetersCovered", max: "maxSquareMetersCovered" },
  { label: "m² totales", min: "minSquareMetersTotal", max: "maxSquareMetersTotal" },
  { label: "Antigüedad", min: "minAntiquity", max: "maxAntiquity", unit: "años" },
  { label: "Cocheras", min: "minParking" },
];

const AMENITY_FILTERS = [
  { key: "hasPool", label: "Pileta" },
  { key: "hasGrill", label: "Parrilla" },
  { key: "hasGarden", label: "Jardín" },
  { key: "hasTerrace", label: "Terraza" },
  { key: "hasBalcony", label: "Balcón" },
];

const isActiveFilter = (value) => value !== null && value !== undefined && value !== false && value !== "";

function formatFilterRange(min, max, unit) {
  const hasMin = isActiveFilter(min);
  const hasMax = isActiveFilter(max);
  if (!hasMin && !hasMax) return null;
  const withUnit = (value) => (unit ? `${value} ${unit}` : String(value));
  if (hasMin && hasMax) return unit ? `${min}–${max} ${unit}` : `${min}–${max}`;
  if (hasMin) return `desde ${withUnit(min)}`;
  return `hasta ${withUnit(max)}`;
}

export function describeFilterExpansion(current = {}, next = {}) {
  const removed = [];
  const adjusted = [];

  RANGE_FILTERS.forEach((group) => {
    const before = formatFilterRange(current[group.min], current[group.max], group.unit);
    const after = formatFilterRange(next[group.min], next[group.max], group.unit);
    if (before === after) return;
    if (before && !after) removed.push({ label: group.label });
    else if (before && after) adjusted.push({ label: group.label, from: before, to: after });
  });

  AMENITY_FILTERS.forEach((amenity) => {
    if (current[amenity.key] && !next[amenity.key]) removed.push({ label: amenity.label });
  });

  const kept = [];
  if (isActiveFilter(current.center) || isActiveFilter(current.searchValue)) kept.push("la zona");
  if (isActiveFilter(current.transactionType)) kept.push("la operación");
  if (isActiveFilter(current.typeProperty)) kept.push("el tipo de propiedad");
  if (isActiveFilter(current.minPrice) || isActiveFilter(current.maxPrice) || isActiveFilter(current.currency)) {
    kept.push("el precio");
  }

  return { removed, adjusted, kept };
}

const comparableValues = (filters) => Object.fromEntries(
  Object.entries(filters).filter(([, value]) => value !== null && value !== undefined && value !== false && value !== "")
    .sort(([a], [b]) => a.localeCompare(b)),
);

export function nextFilterExpansion({ filters, base, level }) {
  const current = JSON.stringify(comparableValues(filters));
  for (let nextLevel = level + 1; nextLevel <= MAX_WIDEN_LEVEL; nextLevel += 1) {
    const next = widenFilters(base, nextLevel);
    if (JSON.stringify(comparableValues(next)) !== current) return { filters: next, base, level: nextLevel };
  }
  return null;
}

export function useSearchFilters(initialFilters = {}) {
  const [state, setState] = useState(() => ({ filters: initialFilters, base: initialFilters, level: 0 }));
  const setFilters = useCallback((update) => {
    setState((previous) => {
      const next = typeof update === "function" ? update(previous.filters) : update;
      if (JSON.stringify(next) === JSON.stringify(previous.filters)) return previous;
      // Edits start from the actual applied values, including prior expansion.
      return { filters: next, base: next, level: 0 };
    });
  }, []);
  const nextExpansion = useMemo(() => nextFilterExpansion(state), [state]);
  const expansionChanges = useMemo(
    () => (nextExpansion ? describeFilterExpansion(state.filters, nextExpansion.filters) : { removed: [], adjusted: [], kept: [] }),
    [nextExpansion, state.filters],
  );
  const appliedChanges = useMemo(
    () => (state.level > 0 ? describeFilterExpansion(state.base, state.filters) : { removed: [], adjusted: [], kept: [] }),
    [state.level, state.base, state.filters],
  );
  const widen = useCallback(() => setState(previous => nextFilterExpansion(previous) || previous), []);
  const restoreSearch = useCallback(() => {
    setState((previous) => (
      previous.level === 0 ? previous : { filters: previous.base, base: previous.base, level: 0 }
    ));
  }, []);
  return {
    filters: state.filters,
    setFilters,
    widenLevel: state.level,
    nextWidenLevel: nextExpansion?.level,
    canExpandFilters: Boolean(nextExpansion),
    expansionChanges,
    appliedChanges,
    nextFilters: nextExpansion?.filters ?? null,
    widen,
    restoreSearch,
  };
}
